import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Send, ChevronDown } from 'lucide-react';
import { FaFacebook, FaInstagram, FaTwitter, FaLinkedin, FaYoutube, FaPinterest, FaTiktok, FaWhatsapp, FaTelegram, FaCcVisa, FaCcMastercard, FaCcAmex, FaCcPaypal, FaCcApplePay, FaCcDiscover, FaCcStripe, FaCcJcb } from "react-icons/fa6";
import { useSettings } from '../../hooks/useSettings';
import { API_BASE } from '../../utils/apiConfig';

const iconMap = {
  Facebook: FaFacebook,
  Instagram: FaInstagram,
  Twitter: FaTwitter,
  Linkedin: FaLinkedin,
  Youtube: FaYoutube,
  Pinterest: FaPinterest,
  Tiktok: FaTiktok,
  Whatsapp: FaWhatsapp,
  Telegram: FaTelegram
};

const paymentIconMap = {
  Visa: { Icon: FaCcVisa, color: '#1a1f71' },
  Mastercard: { Icon: FaCcMastercard, color: '#eb001b' },
  Amex: { Icon: FaCcAmex, color: '#2e77bc' },
  'American Express': { Icon: FaCcAmex, color: '#2e77bc' },
  Paypal: { Icon: FaCcPaypal, color: '#003087' },
  PayPal: { Icon: FaCcPaypal, color: '#003087' },
  'Apple Pay': { Icon: FaCcApplePay, color: '#000000' },
  ApplePay: { Icon: FaCcApplePay, color: '#000000' },
  Discover: { Icon: FaCcDiscover, color: '#ff6000' },
  Stripe: { Icon: FaCcStripe, color: '#635bff' },
  Jcb: { Icon: FaCcJcb, color: '#0b4ea2' },
};

const getLogoUrl = (logoPath) => {
  if (!logoPath) return '';
  if (logoPath.startsWith('http')) return logoPath;
  if (logoPath.startsWith('/uploads')) return `${API_BASE}${logoPath}`;
  return logoPath;
};

const Footer = () => {
  const { branding: settingsBranding } = useSettings();
  const [data, setData] = useState(null);
  const [storeBranding, setStoreBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openSections, setOpenSections] = useState({});
  const toggleSection = (key) => setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  useEffect(() => {
    const fetchFooterData = async () => {
      const apiUrl = `/changes/footer.json?v=${Date.now()}`;

      try {
        console.log('Fetching footer from:', apiUrl);
        const response = await fetch(apiUrl);
        if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        const footerData = await response.json();
        setData(footerData);
        setError(null);
      } catch (err) {
        console.error('Fetch footer data failed:', err.message);
        setError(`Connection Failed. Error: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    const fetchStoreBranding = async () => {
      try {
        const res = await fetch(`/changes/store.json?v=${Date.now()}`);
        if (res.ok) {
          const storeData = await res.json();
          setStoreBranding(storeData?.branding || null);
        }
      } catch {
        // silently ignore
      }
    };
    fetchFooterData();
    fetchStoreBranding();
  }, []);

  // Show a basic footer skeleton or nothing if still loading 
  // But make sure it eventually shows up!
  if (loading) return (
    <footer className="bg-slate-50 py-10 text-center text-slate-400 text-xs font-bold uppercase tracking-widest">
      Syncing Storefront Footer...
    </footer>
  );

  // Fallback data if data is null (e.g., fetch failed)
  const branding = { appName: "Create enterprise", themeColor: "#f97316", ...(settingsBranding || {}), ...storeBranding, ...(data?.branding || {}) };
  const footer = data || {
    general: { bgColor: "#f8fafc", textColor: "#64748b", copyright: "© 2026 Create enterprise. All Rights Reserved." },
    contact: { address: "410, The Galleria, Yogi Chowk Ground, Chikuwadi, Nana Varachha, Surat, Gujarat 395011", email: "info.createenterprise@gmail.com", phone: "+91 9727597187" },
    policies: [],
    socialIcons: []
  };

  const { general, contact, policies, socialIcons, navLinks } = footer;

  const appName = branding.appName || "Create enterprise";
  const primaryColor = branding.themeColor || "#f97316";
  const tagline = branding.tagline || "";
  const description = branding.description || general?.description ||
    "Your trusted source for premium products, made with clean, high-quality ingredients.";

  return (
    <footer
      className="pt-10 sm:pt-12 pb-6 border-t border-slate-100 transition-colors duration-300"
      style={{ backgroundColor: general?.bgColor || "#ffffff", color: general?.textColor || "#0f172a" }}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-10 pb-8">

          {/* Column 1: Brand */}
          <div className="sm:col-span-2 lg:col-span-4 space-y-4">
            <div className="flex items-center gap-2.5">
              {getLogoUrl(branding.logo) && (
                <img
                  src={getLogoUrl(branding.logo)}
                  alt={appName}
                  className="h-10 w-auto object-contain"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <div className="flex flex-col leading-none">
                <span className="text-lg font-black tracking-tight text-slate-900">{appName}</span>
                {tagline && <span className="text-[11px] font-medium text-slate-400 mt-1">{tagline}</span>}
              </div>
            </div>
            <p className="text-sm leading-relaxed max-w-xs text-slate-500">
              {description}
            </p>
            <div className="flex flex-wrap items-center gap-3 pt-1">
              {(socialIcons || []).filter(s => s.visible).map((social) => {
                const Icon = iconMap[social.platform];
                if (!Icon) return null;
                return (
                  <a
                    key={social.id}
                    href={social.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.platform}
                    className="w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-all"
                  >
                    <Icon size={15} />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Column 2: Quick Links (navLinks) */}
          <div className="lg:col-span-2 border-b border-slate-100 lg:border-0">
            <button onClick={() => toggleSection('quick')} className="w-full flex items-center justify-between py-3 lg:py-0 lg:cursor-default lg:pointer-events-none">
              <h3 className="text-sm font-bold text-slate-900 lg:mb-4">Quick Links</h3>
              <ChevronDown size={16} className={`text-slate-400 lg:hidden transition-transform ${openSections['quick'] ? 'rotate-180' : ''}`} />
            </button>
            <ul className={`space-y-2.5 pb-3 lg:pb-0 ${openSections['quick'] ? 'block' : 'hidden'} lg:block`}>
              {(navLinks || []).filter(n => n.visible).map((nav) => (
                <li key={nav.id}>
                  <a href={nav.link} className="text-sm text-slate-500 hover:text-slate-900 transition-colors">
                    {nav.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Customer Service (policies) */}
          <div className="lg:col-span-2 border-b border-slate-100 lg:border-0">
            <button onClick={() => toggleSection('service')} className="w-full flex items-center justify-between py-3 lg:py-0 lg:cursor-default lg:pointer-events-none">
              <h3 className="text-sm font-bold text-slate-900 lg:mb-4">Customer Service</h3>
              <ChevronDown size={16} className={`text-slate-400 lg:hidden transition-transform ${openSections['service'] ? 'rotate-180' : ''}`} />
            </button>
            <ul className={`space-y-2.5 pb-3 lg:pb-0 ${openSections['service'] ? 'block' : 'hidden'} lg:block`}>
              {(policies || []).filter(p => p.visible).map((p) => (
                <li key={p.id}>
                  <a href={`/policies/${p.id}`} className="text-sm text-slate-500 hover:text-slate-900 transition-colors">
                    {p.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Contact Us */}
          <div className="lg:col-span-2 border-b border-slate-100 lg:border-0">
            <button onClick={() => toggleSection('contact')} className="w-full flex items-center justify-between py-3 lg:py-0 lg:cursor-default lg:pointer-events-none">
              <h3 className="text-sm font-bold text-slate-900 lg:mb-4">Contact Us</h3>
              <ChevronDown size={16} className={`text-slate-400 lg:hidden transition-transform ${openSections['contact'] ? 'rotate-180' : ''}`} />
            </button>
            <ul className={`space-y-3.5 pb-3 lg:pb-0 ${openSections['contact'] ? 'block' : 'hidden'} lg:block`}>
              {contact?.phone && (
                <li className="flex items-start gap-3">
                  <Phone size={16} className="text-slate-400 mt-0.5 shrink-0" />
                  <a href={`tel:${contact.phone}`} className="text-sm text-slate-600 hover:text-slate-900 transition-colors">{contact.phone}</a>
                </li>
              )}
              {contact?.email && (
                <li className="flex items-start gap-3">
                  <Mail size={16} className="text-slate-400 mt-0.5 shrink-0" />
                  <a href={`mailto:${contact.email}`} className="text-sm text-slate-600 hover:text-slate-900 transition-colors break-all">{contact.email}</a>
                </li>
              )}
              {false && contact?.address && (
                <li className="flex items-start gap-3">
                  <MapPin size={16} className="text-slate-400 mt-0.5 shrink-0" />
                  <span className="text-sm text-slate-600 leading-relaxed">{contact.address}</span>
                </li>
              )}
            </ul>
          </div>

          {/* Column 5: Stay Updated */}
          <div className="lg:col-span-2">
            <button onClick={() => toggleSection('updated')} className="w-full flex items-center justify-between py-3 lg:py-0 lg:cursor-default lg:pointer-events-none">
              <h3 className="text-sm font-bold text-slate-900 lg:mb-4">Stay Updated</h3>
              <ChevronDown size={16} className={`text-slate-400 lg:hidden transition-transform ${openSections['updated'] ? 'rotate-180' : ''}`} />
            </button>
            <div className={`pb-3 lg:pb-0 ${openSections['updated'] ? 'block' : 'hidden'} lg:block`}>
            <p className="text-sm text-slate-500 mb-3 leading-relaxed">
              Subscribe to get exclusive offers and health tips.
            </p>
            <form className="flex items-center bg-slate-100 rounded-lg p-1" onSubmit={(e) => e.preventDefault()}>
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 min-w-0 bg-transparent px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400"
              />
              <button
                type="submit"
                aria-label="Subscribe"
                className="shrink-0 w-9 h-9 rounded-md text-white flex items-center justify-center transition-opacity hover:opacity-90"
                style={{ backgroundColor: primaryColor }}
              >
                <Send size={16} />
              </button>
            </form>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-6 border-t border-slate-100">
          <p className="text-xs text-slate-400 text-center md:text-left">
            {general?.copyright || "© 2026 Create enterprise. All Rights Reserved."}
          </p>
          <div className="flex items-center gap-2.5">
            {(footer.paymentIcons || []).filter(p => p.visible).map((pay) => {
              const entry = paymentIconMap[pay.platform];
              if (entry) {
                const { Icon, color } = entry;
                return (
                  <span key={pay.id} className="flex items-center justify-center h-7 w-11 bg-white border border-slate-200 rounded-md" title={pay.platform}>
                    <Icon size={26} style={{ color }} />
                  </span>
                );
              }
              if (pay.platform === 'UPI' || pay.platform === 'Upi') {
                return (
                  <span key={pay.id} className="flex items-center justify-center h-7 w-14 bg-white border border-slate-200 rounded-md overflow-hidden px-1" title="UPI">
                    <img src="/upi-logo.svg" alt="UPI" style={{height:'20px',width:'auto'}} />
                  </span>
                );
              }
              return (
                <span key={pay.id} className="px-2.5 py-1 bg-slate-50 border border-slate-200 rounded text-[10px] font-bold text-slate-500">
                  {pay.platform}
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
